import { useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Send SMS Modal — Twilio outbound from Cockpit.
 * Trial-account safe: shows Twilio verified-recipient error clearly.
 */
const SendSMSModal = ({ open, onClose }) => {
  const [to, setTo] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  if (!open) return null;

  const submit = async () => {
    setErr("");
    setResult(null);
    if (!to.startsWith("+")) {
      setErr("Phone number must start with + and country code (e.g., +18178889999).");
      return;
    }
    if (!body.trim()) {
      setErr("Message body is required.");
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/sms/send`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to, body }),
      });
      const d = await r.json();
      if (r.ok) {
        setResult(d.message);
        setBody("");
      } else {
        setErr(d.detail || `HTTP ${r.status}`);
      }
    } catch (e) {
      setErr("Network error sending SMS.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(2,8,20,0.78)",
                  display: "flex", justifyContent: "center", alignItems: "flex-start",
                  padding: "3rem 1.5rem", zIndex: 9998, overflowY: "auto" }}
         data-testid="sms-modal-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: "linear-gradient(160deg, #0a1929 0%, #1b2a4e 100%)",
                    border: "1px solid rgba(90,200,216,0.5)",
                    borderRadius: 10, maxWidth: 600, width: "100%",
                    padding: "1.6rem 1.8rem", color: "#F5EFE1",
                    boxShadow: "0 40px 80px rgba(0,0,0,0.5)" }}
           data-testid="sms-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.9rem" }}>
          <div>
            <div style={{ color: "#5AC8D8", fontSize: "0.68rem", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 800 }}>
              Twilio · Outbound
            </div>
            <h2 style={{ margin: "0.2rem 0 0", fontSize: "1.3rem", fontWeight: 700 }}>💬 Send SMS</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="sms-close"
                  style={{ background: "transparent", color: "#5AC8D8", border: "1px solid #5AC8D8",
                           padding: "0.4rem 0.85rem", borderRadius: 4, fontSize: "0.78rem",
                           fontWeight: 700, cursor: "pointer" }}>Close ✕</button>
        </div>

        <div style={{ background: "rgba(255,179,71,0.1)", border: "1px solid rgba(255,179,71,0.3)",
                      color: "#FFB347", padding: "0.55rem 0.8rem", borderRadius: 4,
                      fontSize: "0.78rem", marginBottom: "0.9rem" }}>
          <strong>Trial-account note:</strong> messages only send to numbers you&apos;ve verified in Twilio Console → Verified Caller IDs. Upgrade Twilio when ready to text anyone.
        </div>

        <label style={{ display: "block", color: "#B8915A", fontSize: "0.7rem", fontWeight: 800,
                        letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 4 }}>To (E.164)</label>
        <input data-testid="sms-to" value={to} onChange={(e) => setTo(e.target.value)}
               placeholder="+18178889999"
               style={{ width: "100%", background: "rgba(0,0,0,0.28)", color: "#F5EFE1",
                        border: "1px solid rgba(90,200,216,0.3)", borderRadius: 4,
                        padding: "0.55rem 0.8rem", fontSize: "0.9rem", marginBottom: "0.9rem" }} />

        <label style={{ display: "block", color: "#B8915A", fontSize: "0.7rem", fontWeight: 800,
                        letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 4 }}>Message</label>
        <textarea data-testid="sms-body" value={body} onChange={(e) => setBody(e.target.value)}
                  rows={5} maxLength={1600}
                  placeholder="Hi — this is Shannon at Moovin Forward…"
                  style={{ width: "100%", background: "rgba(0,0,0,0.28)", color: "#F5EFE1",
                           border: "1px solid rgba(90,200,216,0.3)", borderRadius: 4,
                           padding: "0.55rem 0.8rem", fontSize: "0.9rem", resize: "vertical" }} />
        <div style={{ color: "#9CA3AF", fontSize: "0.72rem", textAlign: "right", marginTop: 3 }}>
          {body.length}/1600
        </div>

        {err && (
          <div data-testid="sms-error" style={{ background: "rgba(220,38,38,0.14)", border: "1px solid rgba(220,38,38,0.4)",
                                                color: "#FCA5A5", padding: "0.55rem 0.8rem", borderRadius: 4,
                                                fontSize: "0.82rem", marginTop: "0.7rem" }}>
            ⚠ {err}
          </div>
        )}

        {result && (
          <div data-testid="sms-success" style={{ background: "rgba(0,220,120,0.12)", border: "1px solid rgba(0,220,120,0.4)",
                                                  color: "#B8F4CE", padding: "0.55rem 0.8rem", borderRadius: 4,
                                                  fontSize: "0.82rem", marginTop: "0.7rem" }}>
            ✓ Sent to {result.to} · status: {result.status || "queued"} · sid: {result.twilio_sid}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
          <button type="button" onClick={submit} disabled={sending}
                  data-testid="sms-send"
                  style={{ background: sending ? "rgba(90,200,216,0.4)" : "linear-gradient(90deg, #5AC8D8, #3A9EAD)",
                           color: "#062617", border: "none", padding: "0.6rem 1.4rem", borderRadius: 5,
                           fontSize: "0.88rem", fontWeight: 800, cursor: sending ? "not-allowed" : "pointer" }}>
            {sending ? "Sending…" : "💬 Send SMS"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendSMSModal;
